import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "@/components/marketing/MarketingShell";
import HeroSlider from "@/components/marketing/HeroSlider";
import { Users, BarChart3, ArrowRight, Heart, UserPlus, Star, FileText, Users2, PieChart, Check } from "lucide-react";
import SoftImage from "@/components/SoftImage";

const STATS = [
  { Ic: Users, v: "10,000+", l: "Children Supported" },
  { Ic: Heart, v: "2,500+", l: "Families Empowered" },
  { Ic: UserPlus, v: "500+", l: "Care Team Professionals" },
  { Ic: BarChart3, v: "95%", l: "Provider Satisfaction" },
  { Ic: Star, v: "Better Tomorrows", l: "For Brighter Futures" },
];

const FEATURES = [
  {
    Ic: FileText, tint: "#1a5fd0", bg: "#e8f1ff", img: "/generated/feature_clinical.png", title: "Clinical Workflow & Practice Management",
    sub: "Everything you need to deliver exceptional care.",
    items: [
      ["Pediatric Electronic Health Records (EHR)", "Digital charting for developmental milestones, ABA logs, and speech/OT notes."],
      ["Smart Telehealth", "HIPAA-compliant video with interactive whiteboards, digital puppets, and more."],
      ["Interactive Treatment Planner", "AI-assisted individualized care plans."],
      ["Automated Scheduling", "Multi-party calendar coordination for parents, therapists, and school staff."],
    ],
  },
  {
    Ic: Users2, tint: "#2f9e57", bg: "#e6f7ec", img: "/generated/feature_collab.png", title: "Collaboration & Engagement Portals",
    sub: "Stronger support through connection.",
    items: [
      ["Parent Coaching Hub", "On-demand lessons, Masterclasses, and at-home exercises."],
      ["School & IEP Coordinator", "Teacher portal to view IEPs, log behaviors, and message the care team."],
      ["Care Team Chat", "Secure messaging for parents, pediatricians, teachers, and specialists."],
    ],
  },
  {
    Ic: PieChart, tint: "#7c53d6", bg: "#f0eaff", img: "/generated/feature_monitor.png", title: "Patient Monitoring & Diagnostics",
    sub: "Turn insights into brighter futures.",
    items: [
      ["Digital Screeners & Assessments", "Automated delivery and scoring for M-CHAT, PHQ-A, GAD-7, and Vanderbilt scales."],
      ["Daily Mood & Behavior Tracker", "Child-friendly or parent-logged tracking for sleep, anxiety, and more."],
      ["AI-Driven Analytics", "Dashboards that flag behavioral drops and highlight positive milestones."],
    ],
  },
];

export default function Landing() {
  return (
    <MarketingShell>
      <HeroSlider />

      {/* STATS STRIP */}
      <section className="relative z-20 -mt-10 lg:-mt-12 mx-auto max-w-[1320px] px-5 lg:px-8">
        <div className="card-soft card-soft-lg px-6 lg:px-9 py-7">
          <div className="flex flex-col xl:flex-row xl:items-center gap-6">
            <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-6">
              {STATS.map(({ Ic, v, l }, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="h-11 w-11 rounded-2xl bg-[#eaf2ff] grid place-items-center text-[#1a5fd0] shrink-0"><Ic size={20} className={i === 1 ? "fill-[#1a5fd0]" : i === 4 ? "fill-[#1a5fd0]" : ""} /></span>
                  <div><div className="font-display text-[19px] font-extrabold text-[#10233f] leading-tight">{v}</div><div className="text-[13px] text-slate-500 leading-tight">{l}</div></div>
                </div>
              ))}
            </div>
            <div className="hidden xl:block w-px self-stretch bg-slate-100" />
            <div className="flex items-center gap-4 xl:max-w-[420px]">
              <SoftImage src="/generated/clinician_female.png" alt="Provider" variant="strong" className="h-16 w-16 shrink-0 rounded-full" imgClassName="rounded-full" />
              <div><p className="text-[14.5px] italic text-slate-600 leading-snug">“ProactiveBH has transformed how we support our families. It's intuitive, comprehensive, and truly puts children first.”</p><p className="mt-1.5 text-[13px] font-semibold text-[#1a5fd0]">— Pediatric Behavioral Health Provider</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="pbh-section mt-8 pt-16 pb-14 overflow-hidden">
        <div className="pbh-grid-lines" />
        <div className="pbh-orb" style={{ width: 320, height: 320, left: "-4%", top: 40, background: "#7fb0ff" }} />
        <div className="pbh-orb" style={{ width: 300, height: 300, right: "-3%", top: 120, background: "#b79cf0" }} />
        <div className="relative mx-auto max-w-[1320px] px-5 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-[12px] font-bold tracking-[0.24em] text-[#5b6b86] uppercase">• A Complete Pediatric Behavioral Health Solution</p>
            <h2 className="font-display mt-3 text-[34px] lg:text-[40px] font-extrabold text-[#10233f]">From Assessment to Progress — All in One Platform</h2>
            <p className="mt-4 text-[16px] text-slate-500">Streamline clinical workflows, strengthen collaboration, and support better outcomes with AI-powered tools designed specifically for pediatric behavioral health.</p>
          </div>
          <div className="mt-10 grid lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="card-soft card-hover overflow-hidden relative">
                <div className="relative">
                  <SoftImage src={f.img} alt={f.title} variant="bottom" rounded={false} className="h-44 w-full" />
                  <span className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/85 backdrop-blur grid place-items-center text-[#1a5fd0] shadow-sm hover:bg-white"><ArrowRight size={16} /></span>
                  <span className="absolute -bottom-6 left-6 h-14 w-14 rounded-2xl grid place-items-center shadow-md ring-4 ring-white" style={{ background: f.bg, color: f.tint }}><f.Ic size={26} /></span>
                </div>
                <div className="p-7 pt-9">
                  <h3 className="font-display text-[19px] font-bold" style={{ color: f.tint }}>{f.title}</h3>
                  <p className="mt-1 text-[14px] text-slate-500">{f.sub}</p>
                  <ul className="mt-5 space-y-4">
                    {f.items.map(([t, d], j) => (
                      <li key={j} className="flex gap-3">
                        <span className="mt-0.5 h-5 w-5 rounded-full grid place-items-center shrink-0" style={{ background: f.tint, color: "#fff" }}><Check size={12} /></span>
                        <div><div className="text-[14.5px] font-bold text-[#10233f]">{t}</div><div className="text-[13.5px] text-slate-500 leading-snug">{d}</div></div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link to="/dashboard" data-testid="see-platform" className="btn-primary px-7 py-3.5 text-[16px] font-semibold inline-flex items-center gap-2">See the Platform in Action <ArrowRight size={18} /></Link>
            <Link to="/request-demo" className="btn-ghost-blue px-7 py-3.5 text-[16px] font-semibold">Request a Demo</Link>
            <div className="font-hand text-[26px] text-[#1a5fd0] ml-2 hidden md:flex items-center gap-2"><Heart size={18} className="fill-[#e23b3b] text-[#e23b3b]" /> Healthy Minds Brighter Futures</div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
